import { apiClient } from './client';

// ─── DTOs (mirror src/BRG.Api/DTOs/EngagementMetricsDtos.cs) ─────────────────

export interface AttendanceLoggedByDto {
  userId: number;
  fullName: string;
}

export interface EventPeriodDto {
  eventId: number;
  eventDate: string; // ISO YYYY-MM-DD
  actualAttendees: number;
  attendancePercent: number;
  lastUpdatedBy: AttendanceLoggedByDto | null;
  lastUpdatedAt: string | null;
}

export interface EventAttendanceRowDto {
  eventName: string;
  brgId: number;
  brgName: string;
  brgCurrentMembershipCount: number;
  period1: EventPeriodDto | null;
  period2: EventPeriodDto | null;
}

export interface MembershipTrendDto {
  brgId: number;
  brgName: string;
  period1Count: number | null;
  period2Count: number | null;
  trendPercent: number;
  trendDirection: 'up' | 'down' | 'zero';
}

export interface TotalsDto {
  totalEventsPeriod1: number;
  totalEventsPeriod2: number;
}

export interface CompanyFilterScopeDto {
  active: boolean;
  infoNote: string | null;
}

export interface EngagementMetricsFiltersDto {
  brgScope: number[];
  company: string;
  period1: { start: string; end: string };
  period2: { start: string; end: string };
}

export interface EngagementMetricsDashboardDto {
  filters: EngagementMetricsFiltersDto;
  companyFilterScope: CompanyFilterScopeDto;
  denominatorBasis: 'current_membership';
  totals: TotalsDto;
  membershipTrends: MembershipTrendDto[];
  eventAttendance: EventAttendanceRowDto[];
}

export interface DashboardQuery {
  brgId?: string; // 'all' or numeric id
  company?: string;
  p1Start: string;
  p1End: string;
  p2Start: string;
  p2End: string;
}

export interface LogAttendanceRequestDto {
  eventId: number;
  actualAttendees: number;
}

export interface LogAttendanceBannerDto {
  text: string;
  autoDismissMs: number;
}

export interface LogAttendanceResponseDto {
  eventId: number;
  actualAttendees: number;
  lastUpdatedAt: string;
  lastUpdatedBy: AttendanceLoggedByDto;
  banner: LogAttendanceBannerDto;
}

export interface EventForLogExistingAttendanceDto {
  actualAttendees: number;
  lastUpdatedAt: string;
  lastUpdatedBy: AttendanceLoggedByDto;
}

export interface EventForLogDto {
  eventId: number;
  displayLabel: string;
  eventDate: string;
  eventOrganizer: string | null;
  eventLocation: string;
  brgId: number;
  brgName: string;
  currentMemberCount: number;
  existingAttendance: EventForLogExistingAttendanceDto | null;
}

export interface EventsForLogResponseDto {
  events: EventForLogDto[];
}

function buildDashboardQuery(q: DashboardQuery): string {
  const params = new URLSearchParams();
  if (q.brgId) params.set('brgId', q.brgId);
  if (q.company) params.set('company', q.company);
  params.set('p1Start', q.p1Start);
  params.set('p1End', q.p1End);
  params.set('p2Start', q.p2Start);
  params.set('p2End', q.p2End);
  return params.toString();
}

export interface CompaniesResponseDto {
  companies: string[];
}

export const engagementMetricsApi = {
  dashboard(q: DashboardQuery): Promise<EngagementMetricsDashboardDto> {
    return apiClient.get<EngagementMetricsDashboardDto>(
      `/api/engagement-metrics?${buildDashboardQuery(q)}`
    );
  },

  logAttendance(req: LogAttendanceRequestDto): Promise<LogAttendanceResponseDto> {
    return apiClient.post<LogAttendanceResponseDto>(
      '/api/engagement-metrics/attendance',
      req
    );
  },

  eventsForLog(): Promise<EventsForLogResponseDto> {
    return apiClient.get<EventsForLogResponseDto>(
      '/api/engagement-metrics/events-for-log'
    );
  },

  companies(): Promise<CompaniesResponseDto> {
    return apiClient.get<CompaniesResponseDto>(
      '/api/engagement-metrics/companies'
    );
  },
};
